import {
  ImageOutlined as ImageIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { ChangeEvent, FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { FramerTooltip } from "@ryde/controls";

import {
  IReportChartAxisOption,
  isReportChartPieMode,
  REPORT_CHART_DISPLAY_MODE_OPTIONS,
  ReportChartDisplayMode,
} from "./parts";
import { ReportChartToolbarPanel } from "./report-chart.drawer.styled";

export interface IReportChartToolbarProps {
  dimensionOptions: IReportChartAxisOption[];
  metricOptions: IReportChartAxisOption[];
  dimensionColumnKey: string;
  metricColumnKey: string;
  displayMode: ReportChartDisplayMode;
  maxItemsInput: string;
  displayGrid: boolean;
  hideLabels: boolean;
  canExportImage: boolean;
  onDimensionChange: (value: string) => void;
  onMetricChange: (value: string) => void;
  onDisplayModeChange: (event: SelectChangeEvent<string>) => void;
  onMaxItemsInputChange: (value: string) => void;
  onMaxItemsBlur: () => void;
  onDisplayGridChange: (checked: boolean) => void;
  onHideLabelsChange: (checked: boolean) => void;
  onRefresh: () => void;
  onExportImage: () => void;
}

export const ReportChartToolbar: FC<IReportChartToolbarProps> = ({
  dimensionOptions,
  metricOptions,
  dimensionColumnKey,
  metricColumnKey,
  displayMode,
  maxItemsInput,
  displayGrid,
  hideLabels,
  canExportImage,
  onDimensionChange,
  onMetricChange,
  onDisplayModeChange,
  onMaxItemsInputChange,
  onMaxItemsBlur,
  onDisplayGridChange,
  onHideLabelsChange,
  onRefresh,
  onExportImage,
}) => {
  const { t } = useTranslation();
  const isPieMode = isReportChartPieMode(displayMode);

  const onDimensionSelect = useEvent((event: SelectChangeEvent<string>) => {
    onDimensionChange(event.target.value);
  });

  const onMetricSelect = useEvent((event: SelectChangeEvent<string>) => {
    onMetricChange(event.target.value);
  });

  const onMaxItemsChange = useEvent((event: ChangeEvent<HTMLInputElement>) => {
    onMaxItemsInputChange(event.target.value);
  });

  return (
    <ReportChartToolbarPanel>
      <div className="ReportChartToolbar-Body">
        <div className="ReportChartToolbar-Row ReportChartToolbar-RowAxes">
          <div className="ReportChartToolbar-Field ReportChartToolbar-AxisField">
            <span className="toolbar-caption">{t("reports.chart.Y_AXIS")}</span>
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                id="report-chart-y-axis"
                value={dimensionColumnKey}
                onChange={onDimensionSelect}
                displayEmpty
              >
                {dimensionOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="ReportChartToolbar-Field ReportChartToolbar-AxisField">
            <span className="toolbar-caption">{t("reports.chart.X_AXIS")}</span>
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                id="report-chart-x-axis"
                value={metricColumnKey}
                onChange={onMetricSelect}
                displayEmpty
              >
                {metricOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        <div className="ReportChartToolbar-Row ReportChartToolbar-RowControls">
          <div className="ReportChartToolbar-Field ReportChartToolbar-DisplayMode">
            <span className="toolbar-caption">
              {t("reports.chart.DISPLAY_MODE")}
            </span>
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                id="report-chart-display-mode"
                value={displayMode}
                onChange={onDisplayModeChange}
              >
                {REPORT_CHART_DISPLAY_MODE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="ReportChartToolbar-Field ReportChartToolbar-MaxItems">
            <span className="toolbar-caption">
              {t("reports.chart.MAX_ITEMS")}
            </span>
            <TextField
              id="report-chart-max-items"
              size="small"
              variant="outlined"
              value={maxItemsInput}
              onChange={onMaxItemsChange}
              onBlur={onMaxItemsBlur}
              slotProps={{
                htmlInput: { inputMode: "numeric", pattern: "[0-9]*" },
              }}
            />
          </div>
          <div className="ReportChartToolbar-Field ReportChartToolbar-CheckboxesField">
            <span
              className="toolbar-caption ReportChartToolbar-CheckboxesSpacer"
              aria-hidden="true"
            >
              &nbsp;
            </span>
            <div className="ReportChartToolbar-Checkboxes">
              <FramerTooltip
                content={
                  isPieMode
                    ? t("reports.chart.DISPLAY_GRID_PIE_DISABLED")
                    : t("reports.chart.DISPLAY_GRID")
                }
              >
                <span>
                  <FormControlLabel
                    className="ReportChartToolbar-CheckboxLabel"
                    control={
                      <Checkbox
                        checked={displayGrid}
                        disabled={
                          displayMode === ReportChartDisplayMode.pieChart
                        }
                        onChange={event =>
                          onDisplayGridChange(event.target.checked)
                        }
                        size="small"
                      />
                    }
                    label={t("reports.chart.DISPLAY_GRID")}
                  />
                </span>
              </FramerTooltip>
              <FormControlLabel
                className="ReportChartToolbar-CheckboxLabel"
                control={
                  <Checkbox
                    checked={hideLabels}
                    onChange={event => onHideLabelsChange(event.target.checked)}
                    size="small"
                  />
                }
                label={t("reports.chart.HIDE_LABELS")}
              />
            </div>
          </div>
          <div className="ReportChartToolbar-Actions">
            <FramerTooltip content={t("general.REFRESH")}>
              <span>
                <IconButton
                  aria-label={t("general.REFRESH")}
                  onClick={onRefresh}
                  size="small"
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </FramerTooltip>
            <FramerTooltip content={t("reports.chart.EXPORT_IMAGE")}>
              <span>
                <IconButton
                  aria-label={t("reports.chart.EXPORT_IMAGE")}
                  onClick={onExportImage}
                  disabled={!canExportImage}
                  size="small"
                >
                  <ImageIcon />
                </IconButton>
              </span>
            </FramerTooltip>
          </div>
        </div>
      </div>
    </ReportChartToolbarPanel>
  );
};
